import { useEffect, useState } from "react";
import { Plus, Loader2, Trash2, Download, Save, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import MicButton from "@/components/ui/MicButton";
import { fetchNotes, createNote, updateNote, deleteNote, downloadNotePdf } from "@/lib/notes";

function NoteCard({ dossierId, note, onDeleted }) {
    const [titre, setTitre] = useState(note.titre || "");
    const [date, setDate] = useState((note.date_creation || "").slice(0, 10));
    const [brief, setBrief] = useState(note.brief || "");
    const [contenu, setContenu] = useState(note.contenu || "");
    const [saving, setSaving] = useState(false);
    const [busy, setBusy] = useState(false);

    async function save() {
        setSaving(true);
        try { await updateNote(dossierId, note.id, { titre: titre.trim() || "Note confidentielle", date_creation: date || null, brief, contenu }); toast.success("Note enregistrée"); }
        catch (e) { toast.error("Enregistrement impossible", { description: e.message }); }
        finally { setSaving(false); }
    }
    async function remove() {
        if (!window.confirm("Supprimer définitivement cette note ?")) return;
        setBusy(true);
        try { await deleteNote(dossierId, note.id); onDeleted(note.id); toast.success("Note supprimée"); }
        catch (e) { toast.error(e.message); setBusy(false); }
    }
    async function pdf() {
        setBusy(true);
        try { await downloadNotePdf(dossierId, note.id); }
        catch (e) { toast.error("Export PDF impossible", { description: e.message }); }
        finally { setBusy(false); }
    }

    return (
        <Card data-testid={`note-card-${note.id}`} className="border-slate-200 bg-white shadow-sm"><CardContent className="pt-6 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <Input data-testid="note-titre" value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Titre de la note" className="h-10 bg-background flex-1 min-w-[220px] font-medium" />
                <Input type="date" data-testid="note-date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 bg-background w-44" />
                <Button variant="ghost" size="sm" data-testid="note-delete" onClick={remove} disabled={busy} className="rounded-full text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-1.5">
                <label className="text-sm text-muted-foreground">Briefing</label>
                <div className="flex items-start gap-2">
                    <Textarea data-testid="note-brief" rows={3} value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="Éléments de cadrage, hypothèses, points de vigilance…" className="bg-background resize-y text-sm min-h-[80px]" />
                    <MicButton testId="note-brief-mic" onText={(t) => setBrief((v) => (v ? v + " " : "") + t)} />
                </div>
            </div>
            <div className="space-y-1.5">
                <label className="text-sm text-muted-foreground">Note</label>
                <Textarea data-testid="note-contenu" value={contenu} onChange={(e) => setContenu(e.target.value)} placeholder="Contenu de la note confidentielle…" className="bg-background resize-y text-sm min-h-[400px] font-mono leading-relaxed" />
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
                <Button variant="outline" data-testid="note-pdf" onClick={pdf} disabled={busy} className="rounded-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Télécharger PDF</Button>
                <Button data-testid="note-save" onClick={save} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer</Button>
            </div>
        </CardContent></Card>
    );
}

export default function NotesConfidentielles({ dossierId }) {
    const [notes, setNotes] = useState(null);
    const [error, setError] = useState("");
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        fetchNotes(dossierId).then((r) => setNotes(r.notes)).catch((e) => setError(e.message));
    }, [dossierId]);

    async function create() {
        setCreating(true);
        try { const n = await createNote(dossierId, { titre: "Nouvelle note", contenu: "" }); setNotes((ns) => [n, ...(ns || [])]); }
        catch (e) { toast.error("Création impossible", { description: e.message }); }
        finally { setCreating(false); }
    }

    return (
        <section data-testid="notes-confidentielles-section" className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center"><FileText className="h-4 w-4 text-primary-foreground" strokeWidth={1.75} /></div>
                    <div>
                        <h2 className="font-display text-2xl text-foreground">Notes confidentielles</h2>
                        <p className="text-xs text-muted-foreground">Notes internes du cabinet, non partagées avec le client. Chaque note dispose de son propre briefing, contenu et export PDF.</p>
                    </div>
                </div>
                <Button data-testid="note-new" onClick={create} disabled={creating} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">{creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Nouvelle note</Button>
            </div>
            {error && <p data-testid="notes-error" className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">{error} — vérifiez que la table <code>notes_confidentielles</code> a été créée dans Supabase.</p>}
            {notes === null ? (
                <p className="text-sm text-muted-foreground">Chargement…</p>
            ) : notes.length === 0 ? (
                <div data-testid="notes-empty" className="rounded-md border border-dashed border-border px-6 py-10 text-center text-sm text-muted-foreground">Aucune note pour l&apos;instant. Cliquez sur « Nouvelle note » pour en créer une.</div>
            ) : (
                <div className="space-y-6">{notes.map((n) => <NoteCard key={n.id} dossierId={dossierId} note={n} onDeleted={(id) => setNotes((ns) => ns.filter((x) => x.id !== id))} />)}</div>
            )}
        </section>
    );
}
